import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import { FILEHUB_MODULE_NAME } from '../../../../constants/constants';
import {
  Prospect,
  CreateProspectRequest,
  CreateProspectResponse,
  UpdateProspectRequest,
  UpdateProspectResponse,
  UpdateProspectStatusRequest,
  UpdateProspectStatusResponse,
  UseGetProspectsParams,
  ProspectDetailsResponse,
  ProspectMetricsResponse,
  UseGetProspectMetricsParams,
  UploadProspectDocumentRequest,
  UploadProspectDocumentResponse,
  ExportProspectsRequest,
  ExportProspectsResponse,
  ProspectExportHistoryResponse,
  CheckInAgreementRequest,
  CheckInAgreementResponse,
  ProspectSearchFilters,
  ProspectListMetadataResponse,
} from './dto.prospect';
import { downloadFileFromFilehub } from '../fileUpload/fileUpload';
// TODO: Remove mock data import once API is ready
import mockMetricsData from '../../../../data/prospectDesk/prospectMetrics.json';

/*
|--------------------------------------------------------------------------
| GET PROSPECTS
|--------------------------------------------------------------------------
*/

export const useGetProspects = ({
  page = 0,
  size = 25,
  searchString,
  sortField,
  sortOrder = 'desc',
  statusNames,
  locationIds,
  assignedOwner,
  sourceNames,
  createdFrom,
  createdTo,
  enabled = true,
}: UseGetProspectsParams = {}) => {
  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.PROSPECTS,
      page,
      size,
      searchString,
      sortField,
      sortOrder,
      statusNames,
      locationIds,
      assignedOwner,
      sourceNames,
      createdFrom,
      createdTo,
    ],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
      });

      const body: {
        searchString?: string;
        sortField?: string;
        sortOrder?: string;
      } & ProspectSearchFilters = {};
      if (searchString) body.searchString = searchString;
      if (sortField) body.sortField = sortField;
      if (sortOrder) body.sortOrder = sortOrder;
      if (statusNames?.length) body.statusNames = statusNames;
      if (locationIds?.length) body.locationIds = locationIds;
      if (assignedOwner) body.assignedOwner = assignedOwner;
      if (sourceNames?.length) body.sourceNames = sourceNames;
      if (createdFrom) body.createdFrom = createdFrom;
      if (createdTo) body.createdTo = createdTo;

      const response = await LuppApi.post<
        {
          searchString?: string;
          sortField?: string;
          sortOrder?: string;
        } & ProspectSearchFilters,
        {
          data: Prospect[];
          totalCount: number;
          pageNumber: number;
          pageSize: number;
          currentPageCount: number;
          totalPages: number;
        }
      >(`${LUPP_API_BASE_PATH}/prospects/searches?${queryParams.toString()}`, body, {
        extractData: (response) => response,
      });

      return {
        items: response.data,
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        currentPageCount: response.currentPageCount,
        totalPages: response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| GET PROSPECT LIST METADATA
|--------------------------------------------------------------------------
*/

/**
 * Fetches filter/dropdown metadata (prospect statuses, sources, agency locations) for the
 * PROSPECT module — used to populate the Prospects listing's filter options.
 * @example
 * const { data: metadata } = useGetProspectListMetadata();
 * // metadata.prospectstatus, metadata.prospectsource, metadata.locations
 */
export const useGetProspectListMetadata = (enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROSPECT_LIST_METADATA],
    queryFn: async () => {
      const response = await LuppApi.get<ProspectListMetadataResponse>(
        `${LUPP_API_BASE_PATH}/list-metadata?module=PROSPECT`
      );
      return response;
    },
    staleTime: Infinity,
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| GET PROSPECT BY ID
|--------------------------------------------------------------------------
*/

export const useGetProspectById = (prospectId: string, enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROSPECTS, prospectId],
    queryFn: async () => {
      const response = await LuppApi.get<ProspectDetailsResponse>(
        `${LUPP_API_BASE_PATH}/prospects/${prospectId}`
      );
      // Return full response with prospectDetails and quoteList
      return response;
    },
    enabled: enabled && !!prospectId,
  });
};

/*
|--------------------------------------------------------------------------
| CREATE PROSPECT
|--------------------------------------------------------------------------
*/

export const useCreateProspect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProspectRequest) => {
      const response = await LuppApi.post<CreateProspectRequest, CreateProspectResponse>(
        `${LUPP_API_BASE_PATH}/prospects`,
        data
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch prospects list
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS] });
    },
    onError: (error: Error) => {
      console.error('Failed to create prospect:', error.message);
    },
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE PROSPECT
|--------------------------------------------------------------------------
*/

export const useUpdateProspect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProspectRequest }) => {
      const response = await LuppApi.patch<UpdateProspectRequest, UpdateProspectResponse>(
        `${LUPP_API_BASE_PATH}/prospects/${id}`,
        data
      );
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch prospects list and the specific prospect
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS, variables.id] });
    },
    onError: (error: Error) => {
      console.error('Failed to update prospect:', error.message);
    },
  });
};

/*
--------------------------------------------------------------------------
| CREATE TEST DRIVE CHECK-IN AGREEMENT
--------------------------------------------------------------------------
*/

export const useCreateCheckInAgreement = () => {
  return useMutation({
    mutationFn: async ({
      prospectId,
      testDriveId,
      data,
    }: {
      prospectId: string;
      testDriveId: string;
      data: CheckInAgreementRequest;
    }) => {
      return LuppApi.post<CheckInAgreementRequest, CheckInAgreementResponse>(
        `${LUPP_API_BASE_PATH}/test-drives/prospects/${prospectId}/test-drives/${testDriveId}/check-in-agreements`,
        data
      );
    },
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE PROSPECT STATUS
|--------------------------------------------------------------------------
*/

export const useUpdateProspectStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProspectStatusRequest }) => {
      const response = await LuppApi.post<
        UpdateProspectStatusRequest,
        UpdateProspectStatusResponse
      >(`/${LUPP_API_BASE_PATH}/prospects/status/${id}`, data);
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch prospects list and the specific prospect
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS, variables.id] });
    },
    onError: (error: Error) => {
      console.error('Failed to update prospect status:', error.message);
    },
  });
};

/*
|--------------------------------------------------------------------------
| GET PROSPECT METRICS
|--------------------------------------------------------------------------
*/

export const useGetProspectMetrics = ({
  startDate,
  endDate,
  groupBy = 'month',
  enabled = true,
}: UseGetProspectMetricsParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROSPECT_METRICS, startDate, endDate, groupBy],
    queryFn: async () => {
      // TODO: Uncomment once API is ready
      // const queryParams = new URLSearchParams();
      // if (startDate) queryParams.append('startDate', startDate);
      // if (endDate) queryParams.append('endDate', endDate);
      // if (groupBy) queryParams.append('groupBy', groupBy);
      //
      // const queryString = queryParams.toString();
      // const url = `sales/prospects/v1/prospects/metrics${queryString ? `?${queryString}` : ''}`;
      //
      // const response = await LuppApi.get<ProspectMetricsResponse>(url);
      // return response;

      // Mock data for development
      return mockMetricsData as ProspectMetricsResponse;
    },
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| UPLOAD PROSPECT DOCUMENT
|--------------------------------------------------------------------------
*/

export const useUploadProspectDocument = () => {
  return useMutation({
    mutationFn: async (data: UploadProspectDocumentRequest) => {
      const response = await LuppApi.post<
        UploadProspectDocumentRequest,
        UploadProspectDocumentResponse
      >(`/${LUPP_API_BASE_PATH}/prospects/files/upload`, data);
      return response;
    },
    onError: (error: Error) => {
      console.error('Failed to upload prospect document:', error.message);
    },
  });
};

/*
|--------------------------------------------------------------------------
| EXPORT PROSPECTS
|--------------------------------------------------------------------------
*/

export const useExportProspects = () => {
  return useMutation({
    mutationFn: async (data: ExportProspectsRequest) => {
      const response = await LuppApi.post<ExportProspectsRequest, ExportProspectsResponse>(
        `${LUPP_API_BASE_PATH}/prospects/export`,
        data
      );

      if (response?.fileId) {
        await downloadFileFromFilehub(
          response.fileId,
          response.fileNameOriginal ?? response.fileId,
          FILEHUB_MODULE_NAME.PROSPECT_MANAGEMENT
        );
      }

      return response;
    },
    onError: (error: Error) => {
      console.error('Failed to export prospects:', error.message);
    },
  });
};

/*
--------------------------------------------------------------------------
| GET PROSPECT EXPORT HISTORY
--------------------------------------------------------------------------
*/

export const useGetProspectsExportHistory = (enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROSPECTS_EXPORT_HISTORY],
    queryFn: async () => {
      const response = await LuppApi.get<ProspectExportHistoryResponse>(
        `${LUPP_API_BASE_PATH}/prospects/export/history`
      );

      return response;
    },
    enabled,
  });
};
